/**
 * The lifecycle every queued dialog shares (#130): a Bootstrap modal over an
 * element the dialog built, a hide that is safe during the fade-in, and the
 * cleanup that releases the dialog queue. Each dialog keeps its own content,
 * results and busy rules (its own hide.bs.modal listener).
 */

import Modal from 'bootstrap/js/src/modal.js';

export interface QueuedModalHooks {
  /** Release the dialog queue (the `done` from enqueueDialog). */
  done: () => void;
  /** The modal finished showing and no hide is pending: focus something. */
  onShown?: () => void;
  /** Hidden, or never shown: settle the dialog's default result. */
  onClosed: () => void;
}

export interface QueuedModal {
  /** Close the modal; asked for during the fade-in, it waits for `shown`. */
  hide: () => void;
  /**
   * Show the modal. False if Bootstrap threw: everything is cleaned up and
   * the queue released, so the caller must not start any loading.
   */
  show: () => boolean;
}

export function queuedModal(
  modal: HTMLElement,
  { done, onShown, onClosed }: QueuedModalHooks
): QueuedModal {
  const previouslyFocused =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
  const instance = new Modal(modal, {
    backdrop: true,
    keyboard: true,
    focus: true,
  });

  // Bootstrap ignores hide() while the modal is still fading in, which left
  // it and its backdrop open and the queue blocked (#125, #130).
  let isShown = false;
  let hideWhenShown = false;
  const hide = (): void => {
    if (isShown) instance.hide();
    else hideWhenShown = true;
  };

  const cleanUp = (): void => {
    onClosed();
    try {
      instance.dispose();
    } catch {
      // already unusable
    }
    modal.remove();
  };

  modal.addEventListener('shown.bs.modal', () => {
    isShown = true;
    if (hideWhenShown) {
      instance.hide();
      return;
    }
    onShown?.();
  });
  modal.addEventListener('hidden.bs.modal', () => {
    cleanUp();
    if (previouslyFocused && document.contains(previouslyFocused)) {
      previouslyFocused.focus();
    }
    done();
  });

  const show = (): boolean => {
    try {
      instance.show();
      return true;
    } catch {
      // Never shown, so hidden.bs.modal will not fire: clean up and release.
      cleanUp();
      done();
      return false;
    }
  };

  return { hide, show };
}
